import { XDMWrapper, TARGETS } from '@gt-common/event-logger-web';
import { RailsBaseSchema } from './railsSchemas';
import {
  LOB,
  LOB_CATEGORY,
  PAGE_TYPE_FUNNEL,
  STORE_ID,
  ORGANISATION,
  BU,
  TIMEZONE,
  COUNTRY,
  COUNTRY_CODE,
  LOCUS_TYPE,
  THANK_YOU_EVENT_VALUES,
  BOOKING_STATUSES,
} from './xdmConstants';
import { store } from '../redux/store';
import { pdtClientSchemaV2 } from '../constants/AbConfigKeys';
import { FUNNEL_SOURCE } from '../constants/TrainConstants';

export const setFunnelSource = () => {
  if (typeof window === 'undefined') return FUNNEL_SOURCE.DIRECT;
  const urlParams = new URLSearchParams(window.location.search);
  let source = FUNNEL_SOURCE.DIRECT;
  if (urlParams.get(FUNNEL_SOURCE.SEO_PARAM) === 'true') {
    source = FUNNEL_SOURCE.SEO;
  } else if (urlParams.get(FUNNEL_SOURCE.SEM_PARAM)?.toUpperCase().startsWith(FUNNEL_SOURCE.SEM)) {
    source = FUNNEL_SOURCE.SEM;
  }
  sessionStorage.setItem(FUNNEL_SOURCE.STORAGE_KEY, source);
  return source;
};

export const getFunnelSource = () => {
  if (typeof window === 'undefined') return FUNNEL_SOURCE.DIRECT;
  return sessionStorage.getItem(FUNNEL_SOURCE.STORAGE_KEY) || FUNNEL_SOURCE.DIRECT;
};

export const PDT_SCHEMA_ID_DEFAULT = 167325;
export const PDT_SCHEMA_NAME_DEFAULT = "mmt_rails_b2c_pdt_client_logging";

export const PDT_SCHEMA_ID_V2 = 173469;
export const PDT_SCHEMA_NAME_V2 = "mmt_rails_b2c_pdt_client_logging_v2";

export const getV2PDTclientSchema = () => {
  const state = store?.getState?.();
  return state?.abConfig?.[pdtClientSchemaV2] === true;
};

export const getPdtSchemaId = () => {
  return getV2PDTclientSchema() ? PDT_SCHEMA_ID_V2 : PDT_SCHEMA_ID_DEFAULT;
};

export const getPdtSchemaName = () => {
  return getV2PDTclientSchema() ? PDT_SCHEMA_NAME_V2 : PDT_SCHEMA_NAME_DEFAULT;
};

export const RAILS_CONSTANTS = {
  PAGE_TYPE: PAGE_TYPE_FUNNEL,
  PAGE_NAME: 'rails-listing',
  FUNNEL_STEP: 'listing',
  LOB,
  LOB_CATEGORY,
  TRAVEL_CLASS: 'listing',
  STORE_ID,
  ORGANISATION,
  BU,
  get TEMPLATE_ID() { return getPdtSchemaId(); },
  get TOPIC_NAME() { return getPdtSchemaName(); },
  TIMEZONE,
  get FUNNEL_SOURCE() { return getFunnelSource(); },
  COUNTRY,
  COUNTRY_CODE,
  LOCUS_TYPE,
};

export const generateUUID = () => {
  let d = new Date().getTime();
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    d += performance.now();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const getAdvancePurchaseDays = (dateStr) => {
  if (!dateStr) return '0';
  const year = parseInt(dateStr.substring(0, 4), 10);
  const month = parseInt(dateStr.substring(4, 6), 10) - 1;
  const day = parseInt(dateStr.substring(6, 8), 10);
  const travelDate = new Date(year, month, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return String(Math.floor((travelDate - today) / 86400000));
};

export const buildSearchContext = (fromStation, toStation, travelClass = 'listing') => ({
  from: {
    locus: {
      locus_id: fromStation?.locusCode || fromStation?.code || '',
      locus_type: fromStation?.locusType || LOCUS_TYPE,
      country: COUNTRY,
      locus_v2_id: fromStation?.locusV2Id || '',
      locus_v2_type: fromStation?.locusV2Type || '',
    },
    location: {
      code: fromStation?.code || '',
      name: fromStation?.city || fromStation?.name || '',
      country_code: COUNTRY_CODE,
      country: COUNTRY,
    }
  },
  to: {
    locus: {
      locus_id: toStation?.locusCode || toStation?.code || '',
      locus_type: toStation?.locusType || LOCUS_TYPE,
      country: COUNTRY,
      locus_v2_id: toStation?.locusV2Id || '',
      locus_v2_type: toStation?.locusV2Type || '',
    },
    location: {
      code: toStation?.code || '',
      name: toStation?.city || toStation?.name || '',
      country_code: COUNTRY_CODE,
      country: COUNTRY,
    }
  },
  travel_class: travelClass
});

export const buildEventTrackingContext = ({
  sessionId,
} = {}) => ({
  organisation: RAILS_CONSTANTS.ORGANISATION,
  bu: RAILS_CONSTANTS.BU,
  env: process.env.NODE_ENV === 'development' ? 'development' : 'production',
  template_id: RAILS_CONSTANTS.TEMPLATE_ID,
  topic_name: RAILS_CONSTANTS.TOPIC_NAME,
  timezone: RAILS_CONSTANTS.TIMEZONE,
  funnel_source: RAILS_CONSTANTS.FUNNEL_SOURCE,
  ...(sessionId && { session_id: sessionId }),
});

export const parseAmount = (amount) => {
  if (typeof amount === 'number') return amount;
  if (typeof amount === 'string') {
    const parsed = parseFloat(amount.replace(/[^0-9.]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const PDT_FALLBACK_NA = 'NA';

const toNum = (val, def = 0) =>
  val !== null && val !== undefined && !Number.isNaN(Number(val)) ? Number(val) : def;

const sanitizeEmptyValues = (obj) => {
  if (!obj || typeof obj !== 'object') return;
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      sanitizeEmptyValues(v);
    } else if (v === '' || v === null || v === undefined) {
      obj[k] = PDT_FALLBACK_NA;
    }
  });
};

const normalizeFareShown = (obj) => {
  if (!obj || typeof obj !== 'object') return;
  if ('fare_shown' in obj) {
    obj.fare_shown = toNum(obj.fare_shown);
  }
  Object.values(obj).forEach((v) => {
    if (Array.isArray(v)) v.forEach(normalizeFareShown);
    else if (v && typeof v === 'object') normalizeFareShown(v);
  });
};

const stripNulls = (obj) => {
  if (obj === null || obj === undefined) return undefined;
  if (Array.isArray(obj)) return obj.map(stripNulls).filter((v) => v !== undefined);
  if (typeof obj === 'object') {
    const result = {};
    Object.entries(obj).forEach(([k, v]) => {
      const stripped = stripNulls(v);
      if (stripped !== undefined && stripped !== null) {
        result[k] = stripped;
      }
    });
    return result;
  }
  return obj;
};

export const transformForXDM = (pdtData) => {
  const xdmData = JSON.parse(JSON.stringify(pdtData));
  const eventValue = xdmData?.event_detail?.event_value;

  const bookingInfo = xdmData?.event_detail?.booking_info;
  if (bookingInfo) {
    const thankYouValues = Object.values(THANK_YOU_EVENT_VALUES);
    const isThankYou = thankYouValues.includes(eventValue);
    if (isThankYou) {
      bookingInfo.status = eventValue === THANK_YOU_EVENT_VALUES.SUCCESS ? BOOKING_STATUSES.SUCCESS : BOOKING_STATUSES.FAILURE;
    }
  }

  const searchContext = xdmData?.search_context;
  if (searchContext) {
    if (searchContext.from_date_time != null && !Number.isNaN(Number(searchContext.from_date_time))) {
      searchContext.from_date_time = Number(searchContext.from_date_time);
    } else {
      delete searchContext.from_date_time;
    }
    if (searchContext.to_date_time != null && !Number.isNaN(Number(searchContext.to_date_time))) {
      searchContext.to_date_time = Number(searchContext.to_date_time);
    } else {
      delete searchContext.to_date_time;
    }
    sanitizeEmptyValues(searchContext?.from);
    sanitizeEmptyValues(searchContext?.to);
    const childAges = searchContext?.pax?.details?.child?.ages;
    if (childAges && Array.isArray(childAges)) {
      searchContext.pax.details.child.ages =
        childAges.length === 0
          ? [PDT_FALLBACK_NA]
          : childAges.map((age) => (age == null ? PDT_FALLBACK_NA : String(age)));
    }
  }

  if (xdmData?.event_detail?.components) {
    const comp = xdmData.event_detail.components;
    if (!Array.isArray(comp)) {
      xdmData.event_detail.components = [comp];
    }
  }
  normalizeFareShown(xdmData?.event_detail);

  if (xdmData?.addon_details && Array.isArray(xdmData.addon_details)) {
    xdmData.addon_details = xdmData.addon_details.map((addon) => {
      if (!addon || typeof addon !== 'object') return addon;
      return stripNulls({
        ...addon,
        selected: typeof addon.selected === 'boolean' ? addon.selected : false,
        unitprice: toNum(addon.unitprice),
        discount: toNum(addon.discount),
      });
    });
  }

  if (xdmData?.event_detail) {
    xdmData.event_detail = stripNulls(xdmData.event_detail);
  }

  return xdmData;
};

export const sendXDMEvent = async ({ payload, eventName = 'XDM Event' }) => {
  try {
    await XDMWrapper.logEvent({
      payload,
      schema: RailsBaseSchema,
      otherConfig: {
        lobName: LOB,
        adobeKeyConfig: []
      },
      target: TARGETS.ALL,
      extraOptions: {
        lob: LOB,
        destinationUrl: typeof window !== 'undefined' ? window.location.href : ''
      }
    });
  } catch (error) {
    console.error(`[${eventName}] Error:`, error?.message);
  }
};

export const sendDualXDMEvent = async ({ encryptedPayload, plainTextPayload }) => {
  const commonConfig = {
    schema: RailsBaseSchema,
    otherConfig: {
      lobName: LOB,
      adobeKeyConfig: []
    },
    extraOptions: {
      lob: LOB,
      destinationUrl: typeof window !== 'undefined' ? window.location.href : ''
    }
  };

  try {
    const xdmPayload = transformForXDM(encryptedPayload);
    await XDMWrapper.logEvent({
      ...commonConfig,
      payload: xdmPayload,
      target: TARGETS.ADOBE_DATA_LAYER,
    });
  } catch (error) {
    console.error('sendDualXDMEvent XDM Error:', error.message);
  }

  try {
    await XDMWrapper.logEvent({
      ...commonConfig,
      payload: plainTextPayload,
      target: TARGETS.PDT,
    });
  } catch (error) {
    console.error('sendDualXDMEvent PDT Error:', error.message);
  }
};

export { TARGETS };
